import { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { useForm } from "../../utils/useForm";
import { useNavigate } from "react-router-dom";
import { validateOnboarding } from "../../utils/login";
import TextInput from "./../../components/forms/TextInput";
import BallsBackground from "../../components/BallsBackground";
import DgButton from "../../components/DgButton";
import ProviderLogin from "./ProviderLogin";
import TwoFactorVerification from "./TwoFactorVerification";
import Request from "../../utils/fetchUtils";
import useFragment from "../../utils/useFragment";
import "./../../assets/fonts.css";
import "./../../styles/global.css";
import "./../../styles/register.css";

/**
 * @file Login.jsx
 * @description Handles user authentication and session initialization with temporary password change support
 *
 * @section Responsibilities
 * - Manage user authentication flow
 * - Handle temporary password changes
 * - Handle form validation
 * - Initialize user session
 * - Redirect based on user role
 * - Handle authentication errors
 * - Manage loading states
 *
 * @section Dependencies
 * - sessionUtils: Session management and validation
 * - ROLES: Role definitions and permissions
 * - FormValidation: Input validation and sanitization
 * - React Router: Navigation and routing
 *
 * @section Integration Points
 * - Authentication API: User verification and token generation
 * - Password Change API: Temporary password update
 * - Session Management: Session initialization and persistence
 * - Navigation System: Post-login routing and redirection
 * - Role Management: User role assignment and validation
 *
 * @section State Management
 * - formData: User credentials and form state
 * - passwordChangeData: New password form state
 * - loading: Authentication process status
 * - error: Error messages and validation states
 * - showPasswordChange: Password change form visibility
 * - sessionData: Temporary session data for password change
 *
 * @section Events
 * - onSubmit: Form submission and authentication
 * - onPasswordChange: Password change submission
 * - onError: Error handling and user feedback
 * - onSuccess: Successful login and redirection
 * - onInputChange: Form field updates
 *
 * @section Props
 * @prop {Function} setUserRole - Updates user role in parent component
 */

const Login = ({ setUserRole }) => {
  const fragmentData = useFragment();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [loginCredentials, setLoginCredentials] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  // Form data for normal login
  const [data, setData] = useForm({
    email: "",
    password: "",
  });

  // Form data for password change
  const [passwordChangeData, setPasswordChangeData] = useForm({
    newPassword: "",
    confirmPassword: "",
  });

  const { email, password } = data;
  const { newPassword, confirmPassword } = passwordChangeData;

  const isActive = (path) => {
    if (window.location.pathname == path) {
      return "active";
    }
    return "";
  };

  // Function to handle temporary password change using Cognito session
  const handleTemporaryPasswordChange = async (newPassword) => {
    try {
      const response = await Request(
        "/user/change-temporary-password",
        {
          session: sessionData.session,
          username: sessionData.username,
          newPassword,
          challengeName: sessionData.challengeName,
          challengeParameters: sessionData.challengeParameters,
        },
        "PATCH",
      );

      if (response.ok) {
        return {
          success: true,
          token: response.data.token,
          accessToken: response.data.accessToken,
          userData: response.data.userData,
        };
      } else {
        return {
          success: false,
          error: response.message || "Password change failed",
        };
      }
    } catch (error) {
      console.error("Password change error:", error);
      return {
        success: false,
        error: "Network error occurred",
      };
    }
  };

  const processThirdPartyLogin = async (access_token, id_token) => {
    setLoading(true);
    const res = await Request(
      "/user/auth?isTrirdParty=true",
      {
        access_token,
        id_token,
      },
      "POST",
    );
    setLoading(false);
    if (res.ok) {
      const { userData, token, accessToken } = res.data;
      setUserRole(userData.role);
      localStorage.setItem("jwt", token);
      localStorage.setItem("accessToken", accessToken);
      localStorage.session = JSON.stringify(userData);
      validateOnboarding(userData, navigate);
    }
  };

  const processLogin = async (data) => {
    setLoading(true);
    setError("");

    const res = await Request(
      "/user/auth",
      {
        ...data,
        username: data.email.toLowerCase(),
      },
      "POST",
    );
    setLoading(false);

    if (res.ok) {
      // Check if requires password change
      if (res.data.requiresPasswordChange) {
        setShowPasswordChange(true);
        setSessionData({
          session: res.data.session,
          username: res.data.username || data.email.toLowerCase(),
          challengeName: res.data.challengeName,
          challengeParameters: res.data.challengeParameters,
        });
        setError(
          res.data.message ||
            "You must change your temporary password before continuing",
        );
        return;
      }

      // Check if 2FA is required
      if (res.data.requires2FA) {
        setLoginCredentials(data);
        setShowTwoFactor(true);
        return;
      }

      // Normal login flow
      const { userData, token, accessToken } = res.data;
      setUserRole(userData.role);
      localStorage.setItem("jwt", token);
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("session", JSON.stringify(userData));
      Object.keys(userData).forEach((k) => {
        const value = userData[k];
        const parsedValue =
          typeof value === "object" ? JSON.stringify(value) : value;
        localStorage.setItem(k, parsedValue);
      });
      validateOnboarding(userData, navigate);
    } else {
      setError(res.message);
    }
  };

  const onPasswordChange = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);
    setError("");

    const result = await handleTemporaryPasswordChange(newPassword);

    if (result.success) {
      // Successful change - complete login with new tokens
      const { userData, token, accessToken } = result;
      setUserRole(userData.role);
      localStorage.setItem("jwt", token);
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("session", JSON.stringify(userData));
      Object.keys(userData).forEach((k) => {
        const value = userData[k];
        const parsedValue =
          typeof value === "object" ? JSON.stringify(value) : value;
        localStorage.setItem(k, parsedValue);
      });
      validateOnboarding(userData, navigate);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Función para cancelar el cambio de contraseña y volver al login
  const handleCancelPasswordChange = () => {
    setShowPasswordChange(false);
    setSessionData(null);
    setError("");
    setPasswordChangeData({
      newPassword: "",
      confirmPassword: "",
    });
  };

  useEffect(() => {
    const { access_token, id_token } = fragmentData;
    if (access_token && id_token) {
      processThirdPartyLogin(access_token, id_token);
    }
  }, [fragmentData, processThirdPartyLogin]);

  // Show 2FA verification if required
  if (showTwoFactor && loginCredentials) {
    return (
      <div className="auth-background ">
        <TwoFactorVerification
          credentials={loginCredentials}
          setUserRole={setUserRole}
          onSuccess={() => {
            setShowTwoFactor(false);
            setLoginCredentials(null);
          }}
          onCancel={() => {
            setShowTwoFactor(false);
            setLoginCredentials(null);
            setError("");
          }}
        />
      </div>
    );
  }

  return (
    <BallsBackground>
      <div className="reduced-content reduced-container shadow1 test-61t4614">
        <div className="inner-content">
          <div className="auth-select">
            <div
              className={`auth-option ${isActive("/login")} ${isActive("/")}`}
            >
              <span>Log In</span>
            </div>
            <div
              onClick={() => navigate("/register")}
              className={`auth-option ${isActive("/register")}`}
            >
              <span>Register</span>
            </div>
          </div>
          <div className="content-wrapper">
            {!showPasswordChange ? (
              // Normal login form
              <>
                <div className="heading">
                  <h1 className="title">Log in</h1>
                </div>
                <Grid
                  container
                  rowSpacing={4}
                  columnSpacing={{ xs: 0, sm: 2, md: 12 }}
                >
                  <Grid item xs={12}>
                    <div className="form-field">
                      <TextInput
                        customInput={{
                          type: "email",
                          label: "Email",
                          name: "email",
                          value: email,
                          action: setData,
                          tooltipBool: false,
                        }}
                      />
                    </div>
                    <div className="form-field">
                      <TextInput
                        customInput={{
                          type: "password",
                          label: "Password",
                          name: "password",
                          value: password,
                          action: setData,
                          tooltipBool: false,
                        }}
                      />
                    </div>
                  </Grid>
                </Grid>
                <div className="button-container">
                  {error && <div className="error">{error}</div>}
                  <DgButton
                    category={"primary"}
                    size={"large"}
                    text={"Log in"}
                    action={() => processLogin(data)}
                    loading={loading}
                  />
                </div>
                <br />
                <div
                  className="sign-in-with"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/recover-password")}
                >
                  Forgot your password?
                </div>
                <div className="blocked">
                  <div className="separator"></div>
                  <div>
                    <div className="sign-in-with">or sign in with:</div>
                    <div className="providers-logos">
                      <ProviderLogin provider="Google" />
                      <ProviderLogin provider="Facebook" />
                      <ProviderLogin provider="SignInWithApple" />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              // Password change form
              <>
                <div className="heading">
                  <h1 className="title">Change Password</h1>
                  <p
                    style={{
                      color: "#8e8e93",
                      marginBottom: "30px",
                      fontSize: "16px",
                    }}
                  >
                    You must change your temporary password before continuing.
                  </p>
                </div>
                <Grid
                  container
                  rowSpacing={4}
                  columnSpacing={{ xs: 0, sm: 2, md: 12 }}
                >
                  <Grid item xs={12}>
                    <div className="form-field">
                      <TextInput
                        customInput={{
                          type: "password",
                          label: "New password",
                          name: "newPassword",
                          value: newPassword,
                          action: setPasswordChangeData,
                          tooltipBool: false,
                        }}
                      />
                    </div>
                    <div className="form-field">
                      <TextInput
                        customInput={{
                          type: "password",
                          label: "Confirm new password",
                          name: "confirmPassword",
                          value: confirmPassword,
                          action: setPasswordChangeData,
                          tooltipBool: false,
                        }}
                      />
                    </div>
                  </Grid>
                </Grid>
                <div className="button-container">
                  {error && <div className="error">{error}</div>}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "16px",
                      marginTop: "16px",
                    }}
                  >
                    <DgButton
                      category={"secondary"}
                      size={"large"}
                      text={"Cancel"}
                      action={handleCancelPasswordChange}
                      disabled={loading}
                    />
                    <DgButton
                      category={"primary"}
                      size={"large"}
                      text={loading ? "Changing..." : "Change Password"}
                      action={onPasswordChange}
                      loading={loading}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </BallsBackground>
  );
};

export default Login;
